import { ScanModel } from 'core/models';
import { ScanStatuses } from 'utils/constants';
import { doThrowOnResponse, getToken, parseValue } from 'utils/helper';
import { logger } from 'utils/logger';

const parseScanItems = (data) => {
    return data.Items.map((item) => {
        const newItem = parseValue(item);
        newItem.id = newItem.scanId;
        return new ScanModel(newItem);
    });
};

const isDynamoItem = (item) =>
    item &&
    typeof item === 'object' &&
    Object.values(item).some((value) => value && typeof value === 'object' && ('S' in value || 'N' in value || 'M' in value || 'L' in value));

const parseInventoryChannels = (channels) => {
    if (!channels) return [];
    if (Array.isArray(channels)) {
        return channels.map((channel) => String(channel).trim()).filter(Boolean);
    }
    if (typeof channels === 'string') {
        return channels
            .split(',')
            .map((channel) => channel.trim())
            .filter(Boolean);
    }
    return [];
};

const normalizeInventoryItem = (raw) => ({
    ...raw,
    id: `${raw.seasonPart}__${raw.seasonSort}`,
    name: raw.Name ?? raw.name ?? '',
    size: raw.Size ?? raw.size ?? '',
    dept: raw.Dept ?? raw.dept ?? '',
    deptName: raw.DeptName ?? raw.deptName ?? '',
    styleNo: raw.seasonSort ?? raw['STYLE NO'] ?? '',
    otb: raw.OTB ?? raw.otb ?? '',
    channels: parseInventoryChannels(raw.channels),
    sampleCount: raw.sampleCount ?? '',
    taskCount: raw.taskCount ?? '',
    scanned: raw.scanned ?? '',
    scanUrl: raw.scanUrl ?? '',
    scanLocation: raw.scanLocation ?? '',
    seasonPart: raw.seasonPart ?? '',
    shootDate: raw.shootDate ?? '',
    sessionCodes: raw.sessionCodes ?? [],
    sessionNames: raw.sessionNames ?? [],
    distributionDates: raw.distributionDates ?? [],
    created: raw.created ?? '',
    extractStatus: raw.extractStatus || ScanStatuses.SUCCEEDED.value,
    grade: raw.grade ?? '',
    barcode: raw.barcode ?? '',
    notes: raw.notes ?? '',
    feedReference: raw.feedReference ?? '',
});

const parseInventoryItems = (data) => {
    if (!data?.Items) {
        return [];
    }

    return data.Items.map((item) => {
        const raw = isDynamoItem(item) ? parseValue(item) : item;
        return normalizeInventoryItem(raw);
    });
};

const buildInventoryUrl = (criteria) => {
    let url = `${process.env.REACT_APP_API_URL}/inventory`;

    // if (criteria?.weekStart) {
    //     return `${url}/occurring?weekstart=${criteria.weekStart}`;
    // }

    // if (criteria?.styleCode) {
    //     url += `/style/${criteria.styleCode}`;
    // }
    // if (criteria?.status) {
    //     url += `/${criteria.status}`;
    // }

    return url;
};

const buildInventoryScansUrl = (criteria) => {
    let url = `${process.env.REACT_APP_API_URL}/inventory/scans`;

    if (criteria?.weekStart) {
        return `${url}/occurring?weekstart=${criteria.weekStart}`;
    }

    if (criteria?.styleCode) {
        url += `/style/${criteria.styleCode}`;
    }
    if (criteria?.status) {
        url += `/${criteria.status}`;
    }

    return url;
};

export const getInventory = async (id) => {
    const url = `${process.env.REACT_APP_API_URL}/inventory/${id}`;

    logger.log(' GETTING INVENTORY DATA: ', id);
    return fetch(url, {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'GET',
    })
        .then((response) => response.json())
        .then((data) => {
            const items = parseInventoryItems(data);
            if (items.length) {
                return items[0];
            }
            throw new Error('No inventory found');
        })
        .catch((err) => {
            logger.error('ERROR getting inventory', err);
            throw err;
        });
};

const buildInventoryScanKey = (seasonPart, styleNo, size) => `${seasonPart || ''}__${styleNo || ''}__${size || ''}`.toLowerCase();

const getScanSeasonPart = (scan) => scan.season || scan.eventMatches?.[0] || '';

const normalizeScanToInventoryItem = (scan) => ({
    id: `scan__${scan.scanId || scan.id}`,
    name: scan.descr || scan.sku || scan.styleCode || '',
    styleNo: scan.styleCode || '',
    size: scan.size || '',
    seasonPart: getScanSeasonPart(scan),
    channels: scan.channel ? [scan.channel] : [],
    dept: scan.department || '',
    deptName: scan.department || '',
    otb: '',
    sampleCount: '',
    taskCount: '',
    scanned: scan.created || '',
    scanUrl: scan.url || scan.thumbUrl || '',
    scanLocation: scan.location || '',
    shootDate: '',
    sessionCodes: scan.sessionMatches || [],
    sessionNames: [],
    distributionDates: [],
    created: scan.created || '',
    extractStatus: scan.extractStatus || ScanStatuses.SUCCEEDED.value,
    grade: scan.grade || '',
    barcode: scan.barcode || '',
    notes: scan.notes || '',
    feedReference: scan.feedReference || '',
});

const mergeScanIntoInventory = (inventory, scan) => ({
    ...inventory,
    scanned: inventory.scanned || scan.created,
    scanUrl: inventory.scanUrl || scan.url || scan.thumbUrl,
    scanLocation: inventory.scanLocation || scan.location,
    extractStatus: inventory.extractStatus || scan.extractStatus || ScanStatuses.SUCCEEDED.value,
    grade: inventory.grade || scan.grade || '',
    barcode: inventory.barcode || scan.barcode || '',
    notes: inventory.notes || scan.notes || '',
    feedReference: inventory.feedReference || scan.feedReference || '',
});

export const combineInventoriesWithScans = (inventories, scans) => {
    const scanMap = new Map();

    scans.forEach((scan) => {
        const key = buildInventoryScanKey(getScanSeasonPart(scan), scan.styleCode, scan.size);
        const existing = scanMap.get(key);
        if (!existing || new Date(scan.created) > new Date(existing.created)) {
            scanMap.set(key, scan);
        }
    });

    const inventoryKeys = new Set();
    const merged = inventories.map((item) => {
        const key = buildInventoryScanKey(item.seasonPart, item.styleNo, item.size);
        inventoryKeys.add(key);
        const scan = scanMap.get(key);
        return scan ? mergeScanIntoInventory(item, scan) : item;
    });

    scanMap.forEach((scan, key) => {
        if (!inventoryKeys.has(key)) {
            merged.push(normalizeScanToInventoryItem(scan));
        }
    });

    return merged;
};

const buildInventoryPaginatedUrl = (criteria, cursor) => {
    let url = `${process.env.REACT_APP_API_URL}/inventory`;
    const params = new URLSearchParams();

    // if (criteria?.weekStart) {
    //     params.set('weekstart', criteria.weekStart);
    // }
    // if (criteria?.styleCode) {
    //     params.set('styleCode', criteria.styleCode);
    // }
    // if (criteria?.status) {
    //     params.set('status', criteria.status);
    // }
    if (cursor && cursor.length) {
        params.set('cursor', cursor);
    }

    const query = params.toString();
    if (query) {
        url += `?${query}`;
    }

    return url;
};

export const getInventoriesPaginated = async (criteria, cursor, signal) => {
    const url = buildInventoryPaginatedUrl(criteria, cursor);

    logger.log(' GETTING INVENTORIES PAGINATED DATA: ', url);
    return fetch(url, {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'GET',
        signal: signal ?? null,
    })
        .then((response) => response.json())
        .then((data) => {
            const items = data.Items ? parseInventoryItems(data) : [];
            return { items, nextCursor: data['next-cursor'] === 'nil' ? null : data['next-cursor'] };
        })
        .catch((err) => {
            if (doThrowOnResponse(err)) {
                throw err;
            }
            return { items: [], nextCursor: null };
        });
};

export const getAllInventoriesPaginated = async (criteria, signal) => {
    const items = [];
    let cursor = '';

    do {
        const { items: pageItems, nextCursor } = await getInventoriesPaginated(criteria, cursor, signal);
        items.push(...pageItems);
        cursor = nextCursor ?? '';
    } while (cursor);

    return items;
};

const buildInventoryScansPaginatedUrl = (criteria, cursor) => {
    let url = `${process.env.REACT_APP_API_URL}/inventory/scans`;
    const params = new URLSearchParams();

    if (cursor && cursor.length) {
        params.set('cursor', cursor);
    }

    const query = params.toString();
    if (query) {
        url += `?${query}`;
    }

    return url;
};

const MOCK_SCANS = [
    {
        did: 'df901d1155e7b346',
        location: 'Wellcom South Melbourne',
        thumbUrl: 'https://api.hgio.com.au/beta/scans/r/scans%2F2026%2F7%2F6%2F2026_7_6_8ebb73ec-6797-445a-8f7e-32b253bc95d9-thumb.png',
        url: 'https://api.hgio.com.au/beta/scans/r/scans%2F2026%2F7%2F6%2F2026_7_6_8ebb73ec-6797-445a-8f7e-32b253bc95d9.jpg',
        filepath: 'scans/2026/7/6/2026_7_6_8ebb73ec-6797-445a-8f7e-32b253bc95d9.jpg',
        photoCodes: ['73086284'],
        eventMatches: ['SEA-437126'],
        channel: 'ecomm',
        notes: 'Found product added to seasonal event.',
        lon: '144.9597552',
        lat: '-37.8325311',
        ext: 'jpg',
        barcode: '9362461380574',
        grade: 'Satisfactory',
        feedReference: '73086284',
        sku: '',
        styleCode: '',
        size: '',
        created: '2026-07-06T03:03:01.039113',
        clientCode: 'TAR',
        scanId: '8ebb73ec-6797-445a-8f7e-32b253bc95d9',
        extractStatus: 'SUCCEEDED',
        filename: '2026_7_6_8ebb73ec-6797-445a-8f7e-32b253bc95d9.jpg',
        upBucket: 'target-gatherer',
        direction: 'incoming',
    },
    {
        did: 'abc123def456',
        location: 'Wellcom North Melbourne',
        thumbUrl: 'https://api.hgio.com.au/beta/scans/r/scans%2F2026%2F7%2F5%2F2026_7_5_abc123-thumb.png',
        url: 'https://api.hgio.com.au/beta/scans/r/scans%2F2026%2F7%2F5%2F2026_7_5_abc123.jpg',
        filepath: 'scans/2026/7/5/2026_7_5_abc123.jpg',
        photoCodes: ['12345678'],
        eventMatches: ['SEA-437127'],
        channel: 'catalogue',
        notes: 'Product scanned and matched.',
        lon: '144.9597552',
        lat: '-37.8325311',
        ext: 'jpg',
        barcode: '9362461380001',
        grade: 'Good',
        feedReference: '12345678',
        sku: 'SKU-001',
        styleCode: 'ST001',
        size: 'S',
        created: '2026-07-05T10:15:00.000000',
        clientCode: 'TAR',
        scanId: 'abc123-def456-ghi789',
        extractStatus: 'SUCCEEDED',
        filename: '2026_7_5_abc123.jpg',
        upBucket: 'target-gatherer',
        direction: 'incoming',
    },
];

export const getInventoryScansPaginated = async (criteria, cursor, signal) => {
    const url = buildInventoryScansPaginatedUrl(criteria, cursor);

    logger.log(' GETTING INVENTORY SCANS PAGINATED DATA: ', url);
    return fetch(url, {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'GET',
        signal: signal ?? null,
    })
        .then((response) => response.json())
        .then((data) => {
            let items = data.Items ? parseScanItems(data) : [];
            // TODO: remove mock data once API returns scans
            if (!items.length) {
                items = MOCK_SCANS.map((s) => new ScanModel({ ...s, id: s.scanId }));
            }
            return { items, nextCursor: data['next-cursor'] === 'nil' ? null : data['next-cursor'] };
        })
        .catch((err) => {
            if (doThrowOnResponse(err)) {
                throw err;
            }
            return { items: [], nextCursor: null };
        });
};

export const getAllInventoryScansPaginated = async (criteria, signal) => {
    const items = [];
    let cursor = '';

    do {
        const { items: pageItems, nextCursor } = await getInventoryScansPaginated(criteria, cursor, signal);
        items.push(...pageItems);
        cursor = nextCursor ?? '';
    } while (cursor);

    return items;
};

export const getInventoriesWithScans = async (criteria, signal) => {
    const [inventories, scans] = await Promise.all([
        getAllInventoriesPaginated(criteria, signal),
        getAllInventoryScansPaginated(criteria, signal),
    ]);

    return combineInventoriesWithScans(inventories, scans);
};

export const getInventories = async (criteria) => {
    // this uses the search cursors to fetch the data.
    const url = buildInventoryUrl(criteria);

    logger.log(' GETTING INVENTORIES DATA: ', url);
    return fetch(url, {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'GET',
    })
        .then((response) => response.json())
        .then((data) => {
            const items = parseInventoryItems(data);
            logger.log(items, ' getInventories');
            return items;
        })
        .catch((err) => {
            logger.error('ERROR getting inventories', err);
            throw err;
        });
};

export const getInventoryScan = async (id) => {
    const url = `${process.env.REACT_APP_API_URL}/inventory/scans/${id}`;

    logger.log(' GETTING INVENTORY SCAN DATA: ', id);
    return fetch(url, {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'GET',
    })
        .then((response) => response.json())
        .then((data) => {
            const items = parseScanItems(data);
            if (items.length) {
                return items[0];
            }
            throw new Error('No inventory scan found');
        })
        .catch((err) => {
            logger.error('ERROR getting inventory scan', err);
            throw err;
        });
};

export const getInventoryScans = async (criteria) => {
    const url = buildInventoryScansUrl(criteria);

    logger.log(' GETTING INVENTORY SCANS DATA: ', url);
    return fetch(url, {
        headers: {
            hgio: getToken(),
            'Content-Type': 'application/json',
        },
        method: 'GET',
    })
        .then((response) => response.json())
        .then((data) => {
            const items = parseScanItems(data);
            logger.log(items, ' getInventoryScans');
            return items;
        })
        .catch((err) => {
            logger.error('ERROR getting inventory scans', err);
            throw err;
        });
};

export const updateInventoryScan = async (scan) => {
    const url = scan.id
        ? `${process.env.REACT_APP_API_URL}/inventory/scans/${scan.id}`
        : `${process.env.REACT_APP_API_URL}/inventory/scans`;

    const tmpData = { ...scan };
    delete tmpData.id;
    delete tmpData.filename;
    delete tmpData.location;
    delete tmpData.filepath;
    delete tmpData.lon;
    delete tmpData.lat;
    delete tmpData.scanId;
    delete tmpData.url;
    delete tmpData.upBucket;
    delete tmpData.did;
    delete tmpData.ext;
    delete tmpData.concat;

    const formData = JSON.stringify({
        ...tmpData,
    });

    logger.log(' UPDATING INVENTORY SCAN DATA: ', formData);

    return fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            hgio: getToken(),
        },
        method: 'PUT',
        body: formData,
    })
        .then((response) => response.json())
        .then((data) => {
            logger.log(data);
            if (data.__type) {
                throw new Error('ERROR updating inventory scan');
            }
            return data;
        })
        .catch((err) => {
            logger.error('ERROR updating inventory scan', err);
            throw err;
        });
};
